import { useEffect, useRef, useState } from "react";
import { WS_BASE_URL } from "@/constants/Configs";

// Base message type
export type BaseWebSocketMessage = {
  timestamp: string;
};

// Connected message
export type ConnectedMessage = BaseWebSocketMessage & {
  type: "connected";
  sessionId: string;
};

// Call status message
export type CallStatusMessage = BaseWebSocketMessage & {
  type: "call_status";
  status: "started" | "ended" | "connecting" | "failed";
  message: string;
};

// Customer message (user speaking)
export type CustomerMessage = BaseWebSocketMessage & {
  type: "customer";
  text: string;
  messageId: string;
  order: number;
};

// Service provider message (agent/company speaking)
export type ServiceProviderMessage = BaseWebSocketMessage & {
  type: "service-provider";
  text: string;
  messageId: string;
  order: number;
};

// Union type for all possible WebSocket messages
export type WebSocketMessage =
  | ConnectedMessage
  | CallStatusMessage
  | CustomerMessage
  | ServiceProviderMessage;

// Legacy type for backward compatibility
export type TranscriptMessage = {
  timestamp: string;
  speaker: "customer" | "service-provider" | "system";
  text: string;
  type?: "final" | "partial" | "status";
  messageId?: string;
  order?: number;
};

type UseTranscriptWebSocketProps = {
  sessionId: string | null;
  onMessage?: (_message: WebSocketMessage) => void;
  onConnected?: (_message: ConnectedMessage) => void;
  onCallStatus?: (_message: CallStatusMessage) => void;
  onCustomerMessage?: (_message: CustomerMessage) => void;
  onServiceProviderMessage?: (_message: ServiceProviderMessage) => void;
  onConnectionChange?: (_connected: boolean) => void;
  onError?: (_error: Event) => void;
};

export const useTranscriptWebSocket = ({
  sessionId,
  onMessage,
  onConnected,
  onCallStatus,
  onCustomerMessage,
  onServiceProviderMessage,
  onConnectionChange,
  onError,
}: UseTranscriptWebSocketProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [callStatus, setCallStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = () => {
    if (!sessionId) {
      console.log(
        "WebSocket connection skipped - sessionId or enabled is false"
      );
      return;
    }

    if (
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      console.log("WebSocket already connecting or connected");
      return;
    }

    try {
      const ws = new WebSocket(
        `${WS_BASE_URL}/transcript?sessionId=${sessionId}`
      );
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        onConnectionChange?.(true);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          setMessages((prev) => [...prev, message]);
          onMessage?.(message);

          // Handle specific message types
          switch (message.type) {
            case "connected":
              onConnected?.(message);
              break;

            case "call_status":
              setCallStatus(message.status);
              onCallStatus?.(message);
              break;

            case "customer":
              onCustomerMessage?.(message);
              break;

            case "service-provider":
              onServiceProviderMessage?.(message);
              break;

            default:
              console.log("Unknown message type:", message);
          }
        } catch (parseError) {
          console.error("Failed to parse WebSocket message:", parseError);
          setError("Failed to parse message");
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket disconnected:", event.code, event.reason);
        setIsConnected(false);
        onConnectionChange?.(false);
        wsRef.current = null;

        // Attempt to reconnect if it wasn't a clean close and we haven't exceeded max attempts
        if (
          event.code !== 1000 &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            10000
          ); // Exponential backoff
          console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${
              reconnectAttempts.current + 1
            }/${maxReconnectAttempts})`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (event) => {
        console.error("WebSocket error:", event);
        setError("WebSocket connection error");
        onError?.(event);
      };
    } catch (connectError) {
      console.error("Failed to create WebSocket connection:", connectError);
      setError("Failed to connect");
    }
  };

  const disconnect = () => {
    console.log("Disconnecting WebSocket");

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close WebSocket connection
    if (wsRef.current) {
      wsRef.current.close(1000, "Component unmounted");
      wsRef.current = null;
    }

    setIsConnected(false);
    setMessages([]);
    setCallStatus(null);
    setError(null);
    reconnectAttempts.current = 0;
  };

  const sendMessage = (message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      console.log("WebSocket message sent:", message);
    } else {
      console.warn("WebSocket not connected, cannot send message");
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  // Connect when sessionId and enabled change
  useEffect(() => {
    if (sessionId) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount or dependency change
    return () => {
      disconnect();
    };
  }, [sessionId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return {
    isConnected,
    messages,
    callStatus,
    error,
    sendMessage,
    clearMessages,
    disconnect,
    reconnect: () => {
      disconnect();
      setTimeout(connect, 100);
    },
  };
};
